import React from 'react'
import { connect } from 'react-redux'
import { Dispatch, AnyAction } from 'redux'

import { RootStoreState } from '../../root/index'
import * as actions from '../actions'
import * as apiActions from '../../api/actions'
import { navigateToTourStops, navigateToTourMap } from '../../navigation/actions'
import { Collection, ResponseError } from '../../api/types'
import { getCurrentLanguageCode } from '../../settings/functions'
import { INITIAL_STATE } from '../../lockapp/reducer'
import { CouponCode, CodeStatus } from '../../lockapp/types'
import { getConfiguration } from '../../configuration'
import { RedeemCode } from 'mytours-read-api-typescript'

/**
 * Defines the own props that this container requires to render a collection
 */
export interface OwnProps {
	collectionId: number
	collectionVersion?: number
	accountId?: number
	onBackAction?: (params: any) => void
}

export interface Props extends OwnProps {
	item?: Collection
	hasLoadedStops: boolean
	isLoadingStops: boolean
	errorLoadingStops?: Error
	loading: boolean
	error?: ResponseError
	language: string
	lockCollection?: CodeStatus
}

export interface Actions {
	navigateToTourStop: (version: number, tourTitle: string, accountId?: number) => void
	navigateToTourMap: (version: number, tourTitle: string) => void
	downloadTour: (version: number, language: string, accountId?: number) => void
	loadCollectionDetails: (version: number, language: string, accountId?: number) => void
	validateCode: (couponCode: CouponCode, linkedId: number) => void
}

const mapStateToProps = (state: RootStoreState, ownProps: OwnProps): Props => {
	const { collectionId, collectionVersion, accountId } = ownProps
	// get full collection details
	const collection = state.collectionlist.collectionDetails.get(collectionId)
	//TODO: fix versions
	const item: Collection | undefined = collection && collection.details ? collection.details : undefined

	const collectionItems = state.collectionlist.collectionItems.get(collectionId)

	// Note: this implementation assumes we are storing stops in the same Map as normal collection items (for a CollectionList)
	//TODO: fix versions
	const hasLoadedStops: boolean = collectionItems && collectionItems.items ? true : false

	const language: string = getCurrentLanguageCode(state)

	const manifestItem = state.settings.appManifest?.collections?.find(item => item.id === collectionId)
	const manifestCollectionVersion = manifestItem?.version
	const manifestAccountId = manifestItem?.account_id

	return {
		collectionId,
		collectionVersion: collectionVersion || manifestCollectionVersion,
		accountId: accountId || manifestAccountId,
		item,
		hasLoadedStops,
		isLoadingStops: collectionItems ? collectionItems.loading : false,
		errorLoadingStops: collectionItems ? collectionItems.error : undefined,
		loading: collection ? collection.loading : false,
		error: collection ? collection.error : undefined,
		language,
		lockCollection: (state.lockapp.linkedType === RedeemCode.LinkedTypeEnum.Collection && state.lockapp.linkedId === collectionId) ? state.lockapp.collection : INITIAL_STATE.collection,
	}
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>, ownProps: OwnProps): Actions => ({
	navigateToTourStop: (version: number, tourTitle: string, collectionAccountId?: number) => {
		dispatch(navigateToTourStops({ tourId: ownProps.collectionId, tourVersion: version, collectionAccountId, tourTitle }))
	},
	navigateToTourMap: (version: number, tourTitle: string) => {
		dispatch(navigateToTourMap({ tourId: ownProps.collectionId, tourVersion: version, tourTitle }))
	},
	downloadTour: (version: number, language: string, accountId?: number) => {
		dispatch(
			actions.loadCollectionItems({
				id: ownProps.collectionId,
				version,
				language,
				subtype: 'tour',
				accountId
			}),
		)
	},
	loadCollectionDetails: (version: number, language: string, accountId?: number) => {
		dispatch(actions.loadCollectionDetails({ id: ownProps.collectionId, version, language, accountId }))
	},
	validateCode: (couponCode: CouponCode, linkedId: number) => {
		const platform = getConfiguration().getPlatform()
		const deviceUUID = getConfiguration().getDeviceUUID()

		dispatch(
			apiActions.validateCode.started({
				linkedId,
				linkedType: RedeemCode.LinkedTypeEnum.Collection,
				devicePlatform: platform,
				deviceUdid: deviceUUID,
				couponCode: couponCode.code,
				expireAt: couponCode.expireAt,
			}),
		)
	},
})

export default (Component: React.ComponentType<Props & Actions>) =>
	connect(mapStateToProps, mapDispatchToProps)(Component)
